import type { ReferenceUniverseDetails, GroupInstrumentUniverseRequest } from "@eu.mdotm.api/sphere";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { IconWalls } from "$root/components/IconWall";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueriesWrapperBase } from "$root/components/ReactQueryWrapper";
import { useUniverseWithGroupEntityManagementActions } from "$root/functional-areas/universe/entity-management";
import { SmallUniverseWithGroupSummary } from "$root/functional-areas/universe/SmallUniverseSummary";
import { universeWithGroupsRepository } from "$root/functional-areas/universe/universeWithGroupsRepository";
import { Card } from "$root/widgets-architecture/layout/Card";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router";
import { UniverseDetailsSubtitle, UniverseDetailsTitleActions } from ".";
import { PortfolioStudioTab } from "../PortfoliosStudio/portfolio-studio-tabs";
import {
	UniverseWithGroupsCardContent,
	type UniverseWithGroupsCardContentHandle,
} from "./partials/UniverseWithGroupsCardContent";
import { useRef } from "react";

export default function EditOrNewUniverseWithGroupPage({ universeUuid }: { universeUuid?: string }): React.JSX.Element {
	if (!universeUuid) {
		return <Redirect to={typedUrlForRoute("PortfoliosStudio", { tab: PortfolioStudioTab.Universes })} />;
	}

	return <UniverseWithGroup universeUuid={universeUuid} />;
}

function UniverseWithGroup({ universeUuid }: { universeUuid: string }): React.JSX.Element {
	const universeWithGroupQueries = useQueries({
		queries: [
			universeWithGroupsRepository.summary.getQueryConfig(universeUuid),
			universeWithGroupsRepository.universeWithGroup.getQueryConfig(universeUuid),
		],
	});

	return (
		<ReactQueriesWrapperBase
			queries={universeWithGroupQueries}
			loadingFallback={<IconWalls.LoadingData />}
			errorFallback={<IconWalls.ErrorData />}
		>
			{([summary, universeComposition]) => (
				<UniverseWithGroupInner summary={summary} universeComposition={universeComposition} />
			)}
		</ReactQueriesWrapperBase>
	);
}

function UniverseWithGroupInner({
	universeComposition,
	summary,
}: {
	universeComposition: GroupInstrumentUniverseRequest;
	summary: ReferenceUniverseDetails;
}) {
	const crumbs = useCrumbs();
	const { t } = useTranslation();
	const { push } = useTypedNavigation();
	const entityManagementActions = useUniverseWithGroupEntityManagementActions(summary, {
		onDuplicate: () => push("PortfoliosStudio", { tab: PortfolioStudioTab.Universes }),
		onDelete: () => push("PortfoliosStudio", { tab: PortfolioStudioTab.Universes }),
	});

	const universeCardRef = useRef<UniverseWithGroupsCardContentHandle | null>(null);

	return (
		<>
			<PageHeader
				crumbs={[
					...crumbs.portfolioStudio("Universes"),
					...crumbs.universeName({ uuid: summary.uuid, name: summary.name }),
				]}
				titleAction={
					<UniverseDetailsTitleActions
						entityManagementActions={entityManagementActions}
						universe={summary}
						onEdit={() =>
							push("EditUniverseWithGroups", {
								universeUuidOrNew: summary?.uuid ?? "",
								selectedGroupUuid: universeCardRef.current?.getSelectedGroupUuid() ?? undefined,
							})
						}
					/>
				}
				subTitle={<UniverseDetailsSubtitle universe={summary} />}
			/>

			<div className="mb-5">
				<SmallUniverseWithGroupSummary universe={summary} />
			</div>
			<Card title={t("UNIVERSE.READ_ONLY.NAME")}>
				<UniverseWithGroupsCardContent mode="read" universe={universeComposition} handleRef={universeCardRef} />
			</Card>
		</>
	);
}
